import { Button } from "@/components/ui/button";
import { Job } from "@/lib/schema";
import { ArrowRight } from "lucide-react";
import { calculateDaysAgo } from "@/lib/availableJobsUtils";
import { formatDisplayText, formatRewardAmount } from "@/lib/textUtils";
import { RecruitmentStatusIndicator } from "./RecruitmentStatusIndicator";
import industryIcon from "@/assets/jobs/icon_industry.svg";
import locationIcon from "@/assets/jobs/icon_location.svg";
import calendarIcon from "@/assets/jobs/icon_calendar.svg";
import airPointsIcon from "@/assets/jobs/icon_air_points.svg";

interface AvailableJobCardProps {
  job: Job;
  index: number;
  onReferSomeone: (job: Job) => void;
}

export function AvailableJobCard({ job, index, onReferSomeone }: AvailableJobCardProps) {
  return (
    <div 
      className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow bg-white rounded-lg" 
      data-testid={`job-card-${index}`}
    >
      <div className="p-6">
        {/* Recruitment Status */}
        <div className="mb-4">
          <RecruitmentStatusIndicator status={job.recruitmentStatus} size="md" />
        </div>

        {/* Job Header with Salary Badge */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <h3 className="text-gray-900 mb-2 font-bold text-lg leading-tight tracking-normal font-space-grotesk" style={{textWrap: 'balance', orphans: 2, widows: 2}}>
              {job.title}
            </h3>
            {/* Company name hidden for privacy */}
          </div>
          {job.rewardInAirPoints && job.rewardInAirPoints > 0 && (
            <div className="border border-[#0067D8] text-[#0067D8] px-3 py-1 ml-4 flex-shrink-0 rounded-full flex items-center gap-1 font-semibold text-xs leading-relaxed tracking-wide">
              <img src={airPointsIcon} alt="Air Points" className="h-4 w-4" style={{filter: 'brightness(0) saturate(100%) invert(19%) sepia(100%) saturate(2000%) hue-rotate(210deg) brightness(95%) contrast(100%)'}} />
              R{formatRewardAmount(job.rewardInAirPoints)}
            </div>
          )}
        </div>

        {/* Job Details */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-2">
            <img src={industryIcon} alt="Department" className="h-4 w-4" />
            <span className="text-gray-600 font-medium text-sm leading-relaxed tracking-wide">
              {formatDisplayText(job.category)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <img src={locationIcon} alt="Location" className="h-4 w-4" />
            <span className="text-gray-600 font-medium text-sm leading-relaxed tracking-wide">
              {job.location}
            </span>
          </div>
          {job.salary && (
            <div className="flex items-center gap-2">
              <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-gray-600 font-medium text-sm leading-relaxed tracking-wide">
                {job.salary}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <img src={calendarIcon} alt="Calendar" className="h-4 w-4" />
            <span className="text-gray-500 font-medium text-sm leading-relaxed tracking-wide">
              {calculateDaysAgo(job.createdAt)} Days ago
            </span>
          </div>
          <Button 
            variant="secondary" 
            size="md" 
            onClick={() => onReferSomeone(job)}
            data-testid={`refer-button-${index}`}
          >
             VIEW DETAILS
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
